import { useMemo, useState } from 'react';
import { IconLoader2, IconSend } from '@tabler/icons-react';
import api from '../lib/api';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * One-off operator SMS to explicitly named MSISDNs — competition winners,
 * small service notices. Deliberately NOT a campaign: no audience
 * resolution, no stats. The backend enforces SA MSISDN normalisation,
 * a 100-recipient cap, and the WASPA/CPA marketing-hours guard.
 */

interface DirectSmsResult {
  success: boolean;
  requested: number;
  sent: number;
  failed: number;
  duplicatesRemoved: number;
  errors?: Array<{ msisdn: string; error: string }>;
  marketingHoursWarning: string | null;
}

const RECIPIENTS_MAX = 100;
const MESSAGE_MAX = 918;
const GSM_SINGLE_PART = 160;
const GSM_MULTIPART = 153;
const UCS2_SINGLE_PART = 70;
const UCS2_MULTIPART = 67;

// Split on line breaks / commas / semicolons ONLY — spaces are legal inside
// a formatted number ("+27 71 605 4142"), so they are stripped per entry
// rather than treated as separators.
function parseMsisdns(raw: string): string[] {
  return raw
    .split(/[\r\n,;]+/)
    .map((entry) => entry.replace(/\s+/g, ''))
    .filter((entry) => entry.length > 0);
}

// Conservative GSM-7 detector (basic charset; extension chars like [ ] { }
// already cost 2 septets so they are counted via the regex fallback too).
const GSM7_RE = /^[A-Za-z0-9 @£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑܧ¿äöñüà\n\r]*$/;

function smsParts(message: string): number {
  if (message.length === 0) return 0;
  const gsm = GSM7_RE.test(message);
  const single = gsm ? GSM_SINGLE_PART : UCS2_SINGLE_PART;
  const multi = gsm ? GSM_MULTIPART : UCS2_MULTIPART;
  if (message.length <= single) return 1;
  return Math.ceil(message.length / multi);
}

export function DirectSendPage() {
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [message, setMessage] = useState('');
  const [reference, setReference] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DirectSmsResult | null>(null);

  const msisdns = useMemo(() => parseMsisdns(recipientsRaw), [recipientsRaw]);
  const parts = smsParts(message);
  const canSubmit =
    msisdns.length > 0 &&
    msisdns.length <= RECIPIENTS_MAX &&
    message.trim().length > 0 &&
    message.length <= MESSAGE_MAX &&
    !sending;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.post('/campaigns/direct-sms', {
        msisdns,
        message: message.trim(),
        reference: reference.trim() || undefined,
      });
      setResult(response.data.result as DirectSmsResult);
      setConfirmOpen(false);
    } catch (err: unknown) {
      const axiosMessage = (err as {
        response?: { data?: { message?: string | string[] } };
      })?.response?.data?.message;
      setError(
        Array.isArray(axiosMessage)
          ? axiosMessage.join('; ')
          : axiosMessage || 'Send failed — please retry.',
      );
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Direct Send</h1>
        <p className="text-sm text-muted-foreground">
          Send a one-off SMS to specific numbers — competition winners, small service notices.
          Not for bulk marketing: use Campaigns for audiences.
        </p>
      </div>

      <MainCard>
        <div className="space-y-4 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="direct-send-recipients">
              Recipients{' '}
              <span className="text-muted-foreground">
                ({msisdns.length} of {RECIPIENTS_MAX} max)
              </span>
            </Label>
            <Textarea
              id="direct-send-recipients"
              placeholder={'One number per line (or comma-separated), e.g.\n0716054142\n27716054142'}
              rows={5}
              value={recipientsRaw}
              onChange={(e) => setRecipientsRaw(e.target.value)}
            />
            {msisdns.length > RECIPIENTS_MAX && (
              <p className="text-sm text-destructive">
                Too many recipients — direct send is capped at {RECIPIENTS_MAX}. Use a campaign
                for larger audiences.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="direct-send-message">
              Message{' '}
              <span className="text-muted-foreground">
                ({message.length}/{MESSAGE_MAX} chars, {parts} SMS part{parts === 1 ? '' : 's'})
              </span>
            </Label>
            <Textarea
              id="direct-send-message"
              placeholder="Congratulations! You have won…"
              rows={4}
              maxLength={MESSAGE_MAX}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="direct-send-reference">Reference (optional, for the audit log)</Label>
            <Input
              id="direct-send-reference"
              placeholder="e.g. spar-competition-winner-aug-2026"
              maxLength={120}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>

          <Alert>
            <AlertDescription>
              Marketing hours apply (Mon–Fri 08:00–20:00, Sat 09:00–13:00 SAST; Sundays and
              public holidays blocked). Outside the window, sends are refused under the default
              compliance policy — or delivered with a warning where compliance has configured
              warn-only.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button onClick={() => setConfirmOpen(true)} disabled={!canSubmit}>
            <IconSend className="mr-2 h-4 w-4" />
            Send to {msisdns.length} recipient{msisdns.length === 1 ? '' : 's'}
          </Button>
        </div>
      </MainCard>

      {result && (
        <MainCard>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Send result</h3>
              <Badge variant={result.failed === 0 ? 'default' : 'destructive'}>
                {result.sent} sent / {result.failed} failed
              </Badge>
              {result.duplicatesRemoved > 0 && (
                <Badge variant="secondary">{result.duplicatesRemoved} duplicates removed</Badge>
              )}
            </div>
            {result.marketingHoursWarning && (
              <Alert>
                <AlertDescription>{result.marketingHoursWarning}</AlertDescription>
              </Alert>
            )}
            {result.errors && result.errors.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>MSISDN</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.errors.map((row) => (
                    <TableRow key={row.msisdn}>
                      <TableCell className="font-mono">{row.msisdn}</TableCell>
                      <TableCell>{row.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </MainCard>
      )}

      <Dialog open={confirmOpen} onOpenChange={(open) => !sending && setConfirmOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm direct send</DialogTitle>
            <DialogDescription>
              This sends a real SMS to {msisdns.length} number{msisdns.length === 1 ? '' : 's'}{' '}
              immediately. There is no undo.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border p-3 text-sm whitespace-pre-wrap break-words">
            {message.trim()}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={sending} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconSend className="mr-2 h-4 w-4" />
              )}
              Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
